import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    TemplateRef,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { AcademicGridStore } from "./s25.academic.grid.store";
import { AcademicGrid } from "./s25.academic.grid";
import { NgTemplateOutlet } from "@angular/common";
import { TypedTemplateDirective } from "../../standalone/typed.template.directive";

@TypeManagerDecorator("s25-ng-academic-grids-legend")
@Component({
    selector: "s25-ng-academic-grids-legend",
    template: `
        <ul class="avail-legend">
            <li class="grid-color" data-type="event" [tabindex]="0">Section</li>
            <li class="grid-color" data-type="eventWithLocations" [tabindex]="0">Section With Location(s)</li>
            <li class="grid-color" data-type="closed" [tabindex]="0">Ad Hoc Occurrence</li>
            <li class="grid-color" data-type="blackout" [tabindex]="0">Different Meeting Pattern</li>
            @if (showReserved()) {
                <li class="grid-color" data-type="locationReserved" [tabindex]="0">Location Reserved</li>
            }
            <li class="grid-color" data-border="overlapping" [tabindex]="0">Overlapping Sections</li>
            <li class="grid-color" data-type="standardSchedule" [tabindex]="0">Standard Schedule</li>
            <ng-container *ngTemplateOutlet="issueTemplate(); context: issueContexts.instructorConflicts" />
            @if (showLocResConflicts()) {
                <ng-container *ngTemplateOutlet="issueTemplate(); context: issueContexts.locationConflicts" />
                <ng-container *ngTemplateOutlet="issueTemplate(); context: issueContexts.resourceConflicts" />
            }
            <ng-container *ngTemplateOutlet="issueTemplate(); context: issueContexts.locationUndersized" />
            <ng-container *ngTemplateOutlet="issueTemplate(); context: issueContexts.locationOversized" />
            <ng-container *ngTemplateOutlet="issueTemplate(); context: issueContexts.missingFeaturePrefs" />
            <ng-container *ngTemplateOutlet="issueTemplate(); context: issueContexts.missingPartitionPrefs" />
            <li
                class="grid-color"
                data-type="event"
                data-pattern="conflict"
                [tabindex]="0"
                [class.ignored]="ignored('standardSchedules')"
                [title]="ignored('standardSchedules') ? 'Ignored' : ''"
            >
                <span class="text-background">Standard Schedule Conflict</span>
            </li>
            @if (!!store.sortLocationsByItem()) {
                <li class="grid-color" data-type="eventWithLocations" data-border="focused" [tabindex]="0">
                    Sorted By
                </li>
            }
        </ul>

        <ng-template #issue [template]="issueTemplate()" let-gridType="gridType" let-issueType="issueType">
            <li
                class="grid-color"
                [attr.data-type]="gridType"
                [attr.data-pattern]="issueType"
                [tabindex]="0"
                [class.ignored]="ignored(issueType)"
                [title]="ignored(issueType) ? 'Ignored' : ''"
            >
                <span class="text-background">{{ AcademicGrid.itemIssueTypes[issueType].label }}</span>
            </li>
        </ng-template>
    `,
    styles: `
        ul {
            list-style-type: none;
            padding: 0;
            margin: 0;
            display: grid;
            gap: 0.5rem;
        }

        li {
            margin: 0;
            padding: 0.5em;
            border-radius: 3px;
        }

        .text-background {
            padding: 0.2em;
            background-color: var(--grid-color--background-color);
            border-radius: 3px;
        }

        .ignored {
            opacity: 0.5;
        }

        [data-border] {
            --grid-color--border-width: 2px;
            --grid-color--border-gap: -2px;
            --grid-color--border-radius: 3px;

            &[data-type] {
                --grid-color--border-gap: 2.5px;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [TypedTemplateDirective, NgTemplateOutlet],
})
export class S25AcademicGridsLegendComponent {
    readonly showLocResConflicts = input(false);
    readonly showReserved = input(false);

    readonly issueTemplate = viewChild.required<unknown, TemplateRef<IssueTemplateContext>>("issue", {
        read: TemplateRef,
    });
    readonly store = inject(AcademicGridStore);
    protected readonly AcademicGrid = AcademicGrid;
    readonly issueContexts = {
        instructorConflicts: { gridType: "event", issueType: "instructorConflicts" },
        locationConflicts: { gridType: "eventWithLocations", issueType: "locationConflicts" },
        resourceConflicts: { gridType: "event", issueType: "resourceConflicts" },
        locationUndersized: { gridType: "eventWithLocations", issueType: "locationUndersized" },
        locationOversized: { gridType: "eventWithLocations", issueType: "locationOversized" },
        standardSchedules: { gridType: "event", issueType: "standardScheduleConflict" },
        missingFeaturePrefs: { gridType: "eventWithLocations", issueType: "missingFeaturePrefs" },
        missingPartitionPrefs: { gridType: "eventWithLocations", issueType: "missingPartitionPrefs" },
    } as const satisfies Record<AcademicGrid.ItemIssueType, { gridType: string; issueType: string }>;

    ignored(type: AcademicGrid.ItemIssueType) {
        return this.store.warningsConfig()[type].action === "ignore";
    }
}

type IssueTemplateContext = {
    gridType: string;
    issueType: AcademicGrid.ItemIssueType;
};
